/**
 * BerthDisplayVariants - Centralized configuration for berth occupancy visualization
 *
 * Manages the visual representation of different berth states (transit_boat, free_berth, etc.)
 * across map and timeline views. Provides consistent colors, styling, and appearance
 * for all berth display variants throughout the marina management interface.
 */
export class BerthDisplayVariants {
  // Base CSS classes for interactive elements
  static INTERACTIVE_CLASSES = "focus-visible:stroke transition-colors cursor-pointer";

  // Visual configuration for each display variant
  static VARIANTS = Object.freeze({
    transit_boat: {
      colors: {
        primary: "#fc927c", // sunset-400 from tailwind.config.js
        secondary: "#d4554b", // sunset-500 from tailwind.config.js
      },
      css: "fill-sunset-400",
      category: "boat",
    },
    renter_boat: {
      colors: {
        primary: "#0bb8b0", // teal-500 from tailwind.config.js
        secondary: "#30a2a0", // teal-600 from tailwind.config.js
      },
      css: "fill-teal-500",
      category: "boat",
    },
    owner_boat: {
      colors: {
        primary: "#0f7e76", // teal-700 from tailwind.config.js
        secondary: "#285E61", // Keep existing or use a darker teal
      },
      css: "fill-teal-700",
      category: "boat",
    },
    member_boat: {
      colors: {
        primary: "#2DD4BF", // teal-400
        secondary: "#14B8A6", // teal-500
      },
      css: "fill-teal-500",
      category: "boat",
    },
    guest_boat: {
      colors: {
        primary: "#5EEAD4", // teal-300
        secondary: "#2DD4BF", // teal-400
      },
      css: "fill-teal-400",
      category: "boat",
    },
    other_boat: {
      colors: {
        primary: "#6B7280", // gray-500
        secondary: "#4B5563", // gray-600
      },
      css: "fill-gray-500",
      category: "boat",
    },
    free_berth: {
      colors: {
        primary: "rgba(35, 210, 202, 0.15)", // teal with opacity
        secondary: "#0bb8b0", // teal-500
      },
      css: "border-teal-600",
      category: "empty",
    },
    unlisted_berth: {
      colors: {
        primary: "rgba(160, 174, 192, 0.15)", // gray with opacity
        secondary: "#9CA3AF", // gray-400
      },
      css: "border-gray-400",
      category: "empty",
    },
    free_berth_available_for_subrentals: {
      colors: {
        primary: "rgba(35, 210, 202, 0.0)", // transparent
        secondary: "#0bb8b0", // teal-500
      },
      css: "border-teal-400",
      category: "empty",
    },
    selected: {
      colors: {
        primary: "#EAB308", // yellow-500
        secondary: "rgba(234, 179, 8, 0.4)", // yellow-500 with opacity
      },
      css: "fill-yellow-500 pulse",
      category: "state",
    },
  });

  /**
   * Get complete styling information for a display variant
   * @param {string} variant - The display variant (e.g., 'transit_boat', 'free_berth')
   * @param {string} context - Context of usage ('map', 'timeline', etc.)
   * @returns {Object} Complete styling information
   */
  static getVariantStyles(variant, context = "map") {
    const baseConfig = this.VARIANTS[variant] || this.VARIANTS.free_berth;

    return {
      ...baseConfig,
      cssClasses: `${baseConfig.css} ${this.INTERACTIVE_CLASSES}`,
      context,
    };
  }

  /**
   * Get SVG-specific styling properties
   * @param {string} variant - The display variant
   * @returns {Object} SVG styling properties (fill, stroke, etc.)
   */
  static getSVGStyles(variant) {
    const config = this.VARIANTS[variant] || this.VARIANTS.free_berth;

    const svgStyles = {
      fill: config.colors.primary,
      stroke: config.colors.secondary,
      strokeWidth: "0.8",
      strokeDasharray: "none",
      pointerEvents: "auto",
      cursor: "pointer",
    };

    // Special cases for different variants
    switch (variant) {
      case "free_berth":
      case "unlisted_berth":
        svgStyles.strokeWidth = "0";
        break;

      case "free_berth_available_for_subrentals":
        svgStyles.strokeDasharray = "0.7";
        break;

      case "selected":
        svgStyles.strokeWidth = "0";
        // Set CSS custom properties for pulse animation
        svgStyles["--pulse-color"] = config.colors.primary;
        svgStyles["--pulse-color-light"] = config.colors.secondary;
        break;

      default:
        // No special styling needed for other variants
        break;
    }

    return svgStyles;
  }

  /**
   * Get CSS classes for a display variant
   * @param {string} variant - The display variant
   * @param {string} context - Context of usage
   * @returns {string} Complete CSS class string
   */
  static getCSSClasses(variant, context = "map") {
    return this.getVariantStyles(variant, context).cssClasses;
  }

  /**
   * Get human-readable description for a variant
   * @param {string} variant - The display variant
   * @param {Object} translatedDescriptions - Translated descriptions from Rails (optional)
   * @returns {string} Human-readable description
   */
  static getVariantDescription(variant, translatedDescriptions = {}) {
    // Use translated description if available, otherwise fallback to variant name
    return translatedDescriptions[variant] || variant.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }

  /**
   * Check if a variant represents a boat (vs. empty berth)
   * @param {string} variant - The display variant
   * @returns {boolean} True if variant represents a boat
   */
  static isBoatVariant(variant) {
    const config = this.VARIANTS[variant];
    return config && config.category === "boat";
  }

  /**
   * Apply SVG styles to an element
   * @param {SVGElement} element - The SVG element to style
   * @param {string} variant - The display variant
   */
  static applySVGStyles(element, variant) {
    const styles = this.getSVGStyles(variant);
    const targetElement = element;

    Object.entries(styles).forEach(([property, value]) => {
      if (property.startsWith("--")) {
        // CSS custom properties
        targetElement.style.setProperty(property, value);
      } else if (property === "strokeWidth") {
        targetElement.style.strokeWidth = value;
      } else if (property === "strokeDasharray") {
        targetElement.style.strokeDasharray = value;
      } else {
        targetElement.setAttribute(property, value);
      }
    });
  }

  /**
   * Get all available display variants
   * @returns {Array} Array of variant names
   */
  static getAvailableVariants() {
    return Object.keys(this.VARIANTS);
  }
}
